import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { PencilSimple, Check, X } from "@phosphor-icons/react";
import { Navbar } from "../components/Navbar";
import { PostCard } from "../components/PostCard";
import { Avatar } from "../components/Avatar";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

export default function Profile() {
  const { user, patchUser } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/me/posts");
      setPosts(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startEdit = () => { setName(user?.handle || ""); setEditing(true); };

  const saveName = async () => {
    setSaving(true);
    try {
      const res = await api.put("/me/handle", { handle: name.trim() });
      patchUser({ handle: res.data.handle });
      setEditing(false);
      load();
      toast.success("Your anonymous name was updated 🌿");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update name");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navbar />
      <div className="relative h-48 sm:h-56 overflow-hidden">
        <img src="https://images.unsplash.com/photo-1629196912832-c8d4a01f6c25?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" alt="calm watercolor" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#FDFBF7] via-transparent to-transparent" />
      </div>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 -mt-16 relative">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-[2rem] p-7 shadow-[0_8px_30px_rgba(44,54,43,0.05)] border border-[#E6EBE4]/60 flex items-center gap-5 mb-8"
        >
          <Avatar handle={user?.handle} size={72} />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold tracking-[0.15em] uppercase text-[#8A9A86]">Your anonymous name</p>
            {editing ? (
              <div className="flex items-center gap-2 mt-1">
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={30}
                  autoFocus
                  className="flex-1 min-w-0 bg-[#F4F1E8] border-none rounded-xl px-4 py-2 text-2xl font-semibold text-[#2C362B] focus:ring-2 focus:ring-[#8A9A86] focus:bg-white outline-none"
                  data-testid="handle-edit-input"
                />
                <button onClick={saveName} disabled={saving} className="bg-[#8A9A86] text-white rounded-full p-2 hover:bg-[#6D7A6A] transition-colors disabled:opacity-50" data-testid="handle-save-btn">
                  <Check size={18} weight="bold" />
                </button>
                <button onClick={() => setEditing(false)} className="bg-[#F4F1E8] text-[#6D7A6A] rounded-full p-2 hover:bg-[#EAE6D9] transition-colors" data-testid="handle-cancel-btn">
                  <X size={18} weight="bold" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <h1 className="font-heading text-3xl font-semibold text-[#2C362B] truncate" data-testid="profile-handle">{user?.handle}</h1>
                <button onClick={startEdit} className="text-[#8A9A86] hover:text-[#5E6C5C] transition-colors shrink-0" data-testid="handle-edit-btn" title="Change name">
                  <PencilSimple size={20} weight="duotone" />
                </button>
              </div>
            )}
            <p className="text-[#8A9A86] text-sm mt-1">Only you can see your email. Others only see this name.</p>
          </div>
        </motion.div>

        <h2 className="font-heading text-xl font-medium text-[#2C362B] mb-4">Your posts ({posts.length})</h2>

        {loading ? (
          <p className="text-center text-[#8A9A86] py-12">Loading…</p>
        ) : posts.length === 0 ? (
          <p className="text-center text-[#8A9A86] py-12">You haven't shared anything yet.</p>
        ) : (
          <div className="space-y-5 pb-12" data-testid="my-posts-list">
            {posts.map((p) => (
              <PostCard key={p.id} post={p} onDeleted={(id) => setPosts((x) => x.filter((q) => q.id !== id))} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
